import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: January 2026</p>

        <Card className="p-8 md:p-12">
          <div className="prose prose-lg max-w-none space-y-8">
            
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
              <p>Bullaale AS ("Company", "we", "us", or "our") operates EzyTransfer and is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">2.1 Personal Information</h3>
              <p>We may collect the following personal information:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Full name and date of birth</li>
                <li>Email address and phone number</li>
                <li>Physical address and country of residence</li>
                <li>Government-issued identification documents</li>
                <li>Bank account and payment card information</li>
                <li>Cryptocurrency wallet addresses</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">2.2 Transaction Information</h3>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Transaction history and amounts</li>
                <li>Recipient information</li>
                <li>Payment methods used</li>
                <li>Currency preferences</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">2.3 Technical Information</h3>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Usage data and analytics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Provide and maintain our services</li>
                <li>Process transactions and send related information</li>
                <li>Verify your identity and comply with KYC/AML requirements</li>
                <li>Detect and prevent fraud and unauthorized access</li>
                <li>Communicate with you about services, updates, and promotions</li>
                <li>Improve our services and develop new features</li>
                <li>Comply with legal and regulatory requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Information Sharing and Disclosure</h2>
              <p>We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Service Providers:</strong> Third-party companies that help us operate our services (payment processors, identity verification services)</li>
                <li><strong>Financial Partners:</strong> Banks and payment networks to process transactions</li>
                <li><strong>Regulatory Authorities:</strong> When required by law or to comply with legal processes</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or sale of assets</li>
              </ul>
              <p className="mt-3">We do not sell your personal information to third parties.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Data Security</h2>
              <p>We implement appropriate security measures including:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Secure authentication mechanisms</li>
                <li>Regular security assessments and audits</li>
                <li>Access controls and employee training</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Data Retention</h2>
              <p>We retain your information for as long as necessary to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Provide our services to you</li>
                <li>Comply with legal and regulatory requirements</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Maintain records for audit purposes</li>
              </ul>
              <p className="mt-3">Generally, we retain transaction records for a minimum of 5 years as required by anti-money laundering regulations.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Your Rights</h2>
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete information</li>
                <li><strong>Erasure:</strong> Request deletion of your information (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Object:</strong> Object to certain processing of your information</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. International Data Transfers</h2>
              <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place, including:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Standard contractual clauses approved by relevant authorities</li>
                <li>Adequacy decisions where applicable</li>
                <li>Other lawful transfer mechanisms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Children's Privacy</h2>
              <p>Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on our platform and updating the "Last updated" date.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Contact Us</h2>
              <p>For questions about this Privacy Policy or to exercise your rights, contact us at:</p>
              <p className="mt-3">
                <strong>Bullaale AS - Data Protection</strong><br />
                Email: privacy@ezytransfer.com<br />
                Website: www.ezytransfer.com
              </p>
            </section>

          </div>
        </Card>

        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mt-8">
          <Link to="/about" className="hover:text-accent">About</Link>
          <Link to="/terms" className="hover:text-accent">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-accent font-semibold text-accent">Privacy Policy</Link>
          <Link to="/cookies" className="hover:text-accent">Cookie Policy</Link>
        </div>
      </div>
    </div>
  );
}
